import { Document } from '@/models/document.ts';

export type PVSProvider = 'doc-cirrus' | 'data-cur' | 'tigrais';

export enum AppointmentBookStatus {
    PENDING_BOOKING = 'PENDING_BOOKING',
    BOOKED = 'BOOKED',
    CANCELLED = 'CANCELLED',
    COMPLETED = 'COMPLETED',
    DECLINED = 'DECLINED',
}

export enum AppointmentCancelReason {
    DOCTOR_CANCELLED = 'DOCTOR_CANCELLED',
    PATIENT_CANCELLED = 'PATIENT_CANCELLED',
    OTHER = 'OTHER',
    // Backend-only reason — not selectable by users. Used when appointments
    // are cancelled as part of a series update operation.
    COLLECTION_UPDATE = 'COLLECTION_UPDATE',
}

export interface AppointmentType {
    id?: string; // Deprecated: missing from patient book and cannot be used properly.
    key: string;
    name: string;
    is_mapped: boolean;
}

export interface AppointmentProfessional {
    id: string;
    key: string;
    type_key: string;
}

export enum OnlineAppointmentProvider {
    DOC_CIRRUS = 'doc-cirrus',
    ARZTKONSULTATION = 'arztkonsultation',
}

export interface Symptom {
    name: string;
    key?: string;
}

export enum Priority {
    NONE = 'NONE',
    LOW = 'LOW',
    NORMAL = 'NORMAL',
    IMPORTANT = 'IMPORTANT',
    VERYIMPORTANT = 'VERYIMPORTANT',
    CRITICAL = 'CRITICAL',
}

export interface Appointment {
    id: string; // uuid or dc_schedule_id or data_cur_event_id
    user_id: string; // Eterno user id or instance_id::pms_user_id
    is_eterno_user?: boolean;
    collection_id?: string;
    chain_configuration_id?: string;
    collection_data?: {
        position: number;
        total_appointments: number;
        chain_name?: { en: string; de: string };
    };
    day: string; // ISO DATE: YYYY-MM-DD
    status: AppointmentBookStatus;
    location_key: string;
    is_online: boolean;
    customer_id: string;

    is_active: boolean;
    is_cancellation_confirmed: boolean;
    is_feedback_event_sent: boolean;

    name: string;
    description: string;

    start: string; // ISO DATETIME
    end: string; // ISO DATETIME
    duration: {
        value: number;
        units: string; // always MINUTES
    };

    professional: AppointmentProfessional;

    priority: Priority;

    cancel_reason?: AppointmentCancelReason;
    is_no_show?: boolean;

    pvs_provider: PVSProvider;

    // DOC CIRRUS
    links?: {
        dc_instance_id?: string;

        patient_id: string;
        calendar_id: string;
        schedule_type_id: string;

        schedule_id?: string;
        practitioner_id?: string;
        location_id?: string;
        resource_id?: string;
        conference_id?: string;

        room_id?: string;

        online_url?: string;
        online_url_for_doctor?: string;
        ak_call_id?: string;
    };

    // DATA CUR
    data_cur_links?: {
        instance_id: string;

        patient_id: string;
        resource_id: string;
        event_type_id: string;
        event_type_code: string;
        event_id?: string;

        room_id?: string;

        online_url?: string;
        online_url_for_doctor?: string;
        ak_call_id?: string;
    };

    data: {
        patient_name?: string;
        generated_id?: string;

        documents: Document[];
        info: string;
        symptoms: Symptom[];

        services: AppointmentType[];

        is_for_self: boolean;
        is_returning: boolean;
        is_with_prescription: boolean;
        is_anamnesis_shared?: boolean;
        is_insurance_card_active: boolean;

        online_provider?: OnlineAppointmentProvider;

        insurance_coverage_type?: string; // public, private, selfpayer
    };

    is_external?: boolean;

    // Backend-enriched Prismic CMS documents, keyed by locale (e.g. 'en-us', 'de-de').
    doctor?: Record<string, any>;
    location?: Record<string, any>;

    meta: {
        created_at: string;
        updated_at: string;
    };
}
